"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Error boundary for site-wide floating widgets (chat, cookie banner,
 * tracker, popups, ...) mounted in the [locale] layout.
 *
 * Those widgets sit OUTSIDE the [locale]/error.tsx route boundary, so a
 * render crash in any of them falls through to app/global-error.tsx and
 * replaces the whole page with the "something went wrong" screen. Here a
 * crash just hides that one widget, and the report names the widget and
 * carries React's component stack — global-error only ever gets a
 * minified message like "React error #310" with no hint of where.
 */
export class WidgetBoundary extends Component<
  { name: string; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    try {
      const messageText = error?.message || error?.name || String(error);
      fetch("/api/errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: `Widget error (${this.props.name}) [${error?.name || "Error"}]: ${messageText}`,
          stack: error?.stack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          context: {
            kind: "widget-error-boundary",
            widget: this.props.name,
            componentStack: info.componentStack?.slice(0, 2000),
          },
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}
